/**
 * YoMA™ — Your Mentor Assistant. Sprint 1 Phase 4 (canon v1.0.0, locked).
 *
 * An AI assistant, NOT a human mentor. Helps the learner understand their
 * Living Report™, reflect on their Career DNA™, explore their Future Career
 * Matches™, think through their One Next Step™, and prepare for real
 * conversations with teachers, parents, and mentors.
 *
 * PRESENTATION ONLY: this page reuses the existing chat infrastructure
 * (@ai-sdk/react + the ai-coach edge function) unchanged in shape. The
 * conversation is grounded server-side in the sealed report payload — no
 * scores or recommendations are computed or duplicated here.
 */
import { useState, useRef, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, Sparkles, Loader2, MessageCircle } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import PageHead from "@/components/PageHead";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion } from "framer-motion";

/**
 * Canonical conversation entry points (Phase 4) — grounded on the report,
 * mapped to YoMA™'s six primary purposes. Entry points only; YoMA™ never
 * creates new recommendations, only reflects on ones already in the report.
 */
const SUGGESTIONS = [
  "Help me understand my Career DNA™",
  "Why were these Future Career Matches™ recommended?",
  "Help me think about my One Next Step™",
  "Explain one of my strengths",
  "Help me prepare questions for a mentor",
];

const CoachPage = () => {
  const [searchParams] = useSearchParams();
  // A prompt handed over from the YoMA panel lands in the box, unsent, so the
  // learner stays in control of what they actually ask.
  const [input, setInput] = useState(searchParams.get("q") ?? "");
  const [ready, setReady] = useState(false);
  const tokenRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      tokenRef.current = data.session?.access_token ?? null;
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      tokenRef.current = session?.access_token ?? null;
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Stable transport that reads the current token at request time
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-coach`,
        headers: () => ({
          Authorization: `Bearer ${tokenRef.current ?? ""}`,
        }),
      }),
    [],
  );

  const { messages, sendMessage, status } = useChat({
    transport,
    onError: (err) => {
      console.error(err);
      toast.error("YoMA™ hit a snag. Please try again.");
    },
  });

  const isBusy = status === "submitted" || status === "streaming";

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleSend = async (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || isBusy || !ready) return;
    setInput("");
    await sendMessage({ text: msg });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageHead
        title="YoMA™ — Your Mentor Assistant | WeKIT™ Career Clarity 360"
        description="Reflect on your Living Report™ and prepare for your next mentoring conversation with YoMA™."
        path="/coach"
        noIndex
      />
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2">
            <WeKitLogo size="sm" />
            <span className="font-display text-lg font-bold text-foreground">WeKIT™</span>
          </Link>
          <Button variant="outline" size="sm" asChild>
            <Link to="/report"><ArrowLeft className="mr-1 h-3 w-3" /> Report</Link>
          </Button>
        </div>
      </header>

      <main className="flex-1 container mx-auto max-w-3xl px-4 py-6 flex flex-col">
        <div className="text-center mb-4">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <MessageCircle className="h-6 w-6 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-bold text-foreground">
            YoMA™ — <span className="gradient-text">Your Mentor Assistant</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Grounded in your Living Report™ — here to help you reflect, not to replace it.
          </p>
        </div>

        {/* Boundary, stated plainly and once — never repeated mid-conversation. */}
        <p className="mb-6 text-center text-xs text-muted-foreground/70 max-w-md mx-auto">
          YoMA™ is an AI companion, not a human mentor. The best conversations still
          happen with your teachers, parents, and mentors — YoMA™ helps you prepare for them.
        </p>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto space-y-4 rounded-2xl border border-border/40 bg-card/30 p-4 min-h-[400px]"
        >
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
              <Sparkles className="h-8 w-8 text-primary/60" />
              <p className="text-sm text-muted-foreground text-center max-w-sm">
                Ask YoMA™ anything about your report. Start with a prompt below.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => handleSend(s)}
                    className="rounded-full border border-border/50 bg-background/50 px-3 py-1.5 text-xs text-foreground hover:border-primary/40 hover:bg-primary/5 transition-all"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => {
            const text = m.parts
              .map((p) => (p.type === "text" ? p.text : ""))
              .join("");
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <Card
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border/50"
                  }`}
                >
                  {text || (isBusy ? "…" : "")}
                </Card>
              </motion.div>
            );
          })}

          {isBusy && messages[messages.length - 1]?.role === "user" && (
            <div className="flex justify-start">
              <Card className="px-4 py-3 bg-card border-border/50">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              </Card>
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="mt-4 flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={ready ? "Ask YoMA™…" : "Loading session…"}
            disabled={!ready || isBusy}
            className="flex-1 h-12 rounded-full px-5"
          />
          <Button
            type="submit"
            disabled={!ready || isBusy || !input.trim()}
            size="icon"
            className="h-12 w-12 rounded-full hover-glow"
          >
            {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default CoachPage;
